import React, { useCallback, useEffect } from 'react'
import { Modal } from './Modal'

import '../assets/css/style.css'
import info from '../assets/img/icon/info.svg'
import guide_1 from '../assets/img/tumitate_simulation/guide_1.svg'
import guide_2 from '../assets/img/tumitate_simulation/guide_2.svg'
import guide_3 from '../assets/img/tumitate_simulation/guide_3.svg'
import guide_4 from '../assets/img/tumitate_simulation/guide_4.svg'

interface CalcGuideModalProps {
  isShow: boolean
  setShow: (isShow: boolean) => void
}

export const CalcGuideModal: React.FC<CalcGuideModalProps> = React.memo((props) => {
  const { isShow, setShow } = props
  useEffect(() => {
    if (isShow) {
      document.body.setAttribute('style', 'overflow: hidden;')
    } else {
      document.body.removeAttribute('style')
    }

    return () => document.body.removeAttribute('style')
  }, [isShow])

  const handleHide = useCallback(() => {
    setShow(false)
  }, [setShow])

  return (
    <React.Fragment>
      {isShow && (
        <Modal className="c-modal">
          <div className="c-modal__content">
            <div className="c-modal__head">
              <span className="c-modal__head__icon">
                <img src={info} alt="" />
              </span>
              <h2 className="c-modal__head__title">つみたてシミュレーション 使い方ガイド</h2>
              <span className="c-modal__head__close" onClick={handleHide}></span>
            </div>
            <div className="c-modal__body">
              <div className="c-modal__lead">
                <p>
                  つみたてシミュレーションは、金額や期間などを決めて、いろいろな“つみたて”を計算するツールです
                </p>
              </div>
              <h3 className="c-heading-1">操作方法</h3>
              <p className="u-mb1em">4つのタブの中から1つを選んでください</p>
              <ul>
                <li>
                  ・あなたの資産が将来いくらになるかを知りたい場合には、「将来の評価額」をご利用ください。
                </li>
                <li>
                  ・目標額を達成するために必要な利回りを知りたい場合には、「必要な利回り」をご利用ください。
                </li>
                <li>
                  ・目標額を達成するために必要な投資期間を知りたい場合には、「必要な投資期間」をご利用ください。
                </li>
                <li>
                  ・目標額を達成するために必要な積立額(毎月)を知りたい場合には、「必要な積立額」をご利用ください。
                </li>
              </ul>

              <h3 className="c-heading-1">将来の評価額</h3>
              <p>あなたの資産が将来いくらになるかを計算することができます。</p>
              <h4>操作方法</h4>
              <p>
                {/* eslint-disable-next-line no-irregular-whitespace*/}
                STEP１　５つの条件（初期投資額、積立額（毎月）、期間、利回り（年率）、預金金利（年率））を入力してください。
              </p>
              {/* eslint-disable-next-line no-irregular-whitespace*/}
              <p>STEP２　「計算」をクリック（タップ）してください。</p>
              <h4>計算方法</h4>
              <p>以下の式より目標(X)を求めます。</p>
              <p>
                <img alt="シミュレーションの計算方法" src={guide_1} style={{ height: '350px' }} />
              </p>
              <p>※最初の積立額は初期投資と同時点より運用する計算となっています。</p>

              <h3 className="c-heading-1">必要な利回り</h3>
              <p>
                目標額を達成するために必要な利回りを計算することができます。
                <br />
                ※必要な利回り(年率)は、小数第2位を四捨五入して表示しています
                <br />
                また、目標と評価額が一致しない場合があります。
              </p>
              <h4>操作方法</h4>
              <p>
                {/* eslint-disable-next-line no-irregular-whitespace*/}
                STEP１　５つの条件（目標、初期投資額、積立額（毎月）、期間、預金金利（年率））を入力してください。
              </p>
              {/* eslint-disable-next-line no-irregular-whitespace*/}
              <p>STEP２　「計算」をクリック（タップ）してください。</p>
              <h4>計算方法</h4>
              <p>以下の式より利回り(X)を求めます。</p>
              <p>
                <img alt="シミュレーションの計算方法" src={guide_2} style={{ height: '350px' }} />
              </p>
              <p>※最初の積立額は初期投資と同時点より運用する計算となっています。</p>

              <h3 className="c-heading-1">必要な投資期間</h3>
              <p>
                目標額を達成するために必要な投資期間を計算することができます。
                <br />
                ※月単位で計算を行っている為、目標と評価額が一致しない場合があります。
              </p>
              <h4>操作方法</h4>
              <p>
                {/* eslint-disable-next-line no-irregular-whitespace*/}
                STEP１　５つの条件（目標、初期投資額、積立額（毎月）、利回り（年率）、預金金利（年率））を入力してください。
              </p>
              {/* eslint-disable-next-line no-irregular-whitespace*/}
              <p>STEP２　「計算」をクリック（タップ）してください。</p>
              <h4>計算方法</h4>
              <p>以下の式より期間(X)を求めます。</p>
              <p>
                <img alt="シミュレーションの計算方法" src={guide_3} style={{ height: '350px' }} />
              </p>
              <p>※最初の積立額は初期投資と同時点より運用する計算となっています。</p>

              <h3 className="c-heading-1">必要な積立額</h3>
              <p>
                目標額を達成するために必要な積立額（毎月）を計算することができます。
                <br />
                ※月単位で計算を行っている為、目標と評価額が一致しない場合があります。
              </p>
              <h4>操作方法</h4>
              <p>
                {/* eslint-disable-next-line no-irregular-whitespace*/}
                STEP１　５つの条件（目標、初期投資額、期間、利回り（年率）、預金金利（年率））を入力してください。
              </p>
              {/* eslint-disable-next-line no-irregular-whitespace*/}
              <p>STEP２　「計算」をクリック（タップ）してください。</p>
              <h4>計算方法</h4>
              <p>以下の式より積立額(X)を求めます。</p>
              <p>
                <img alt="シミュレーションの計算方法" src={guide_4} style={{ height: '350px' }} />
              </p>
              <p>※最初の積立額は初期投資と同時点より運用する計算となっています。</p>

              <h3 className="c-heading-1">入力値</h3>
              <p>各項目の入力可能範囲は以下の通りです。</p>
              <table className="c-table">
                <tbody>
                  <tr>
                    <th>入力項目</th>
                    <th>入力可能範囲</th>
                  </tr>
                  <tr>
                    <th>目標</th>
                    <td>0〜999999 万円</td>
                  </tr>
                  <tr>
                    <th>
                      初期投資額<sup>*1</sup>
                    </th>
                    <td>0〜99999.9 万円</td>
                  </tr>
                  <tr>
                    <th>
                      積立額（毎月）<sup>*1</sup>
                    </th>
                    <td>0〜999.9 万円</td>
                  </tr>
                  <tr>
                    <th>期間</th>
                    <td>1〜99 年</td>
                  </tr>
                  <tr>
                    <th>
                      利回り（年率）<sup>*1</sup>
                    </th>
                    <td>0〜99.9 ％</td>
                  </tr>
                  <tr>
                    <th>
                      預金金利（年率）<sup>*2</sup>
                    </th>
                    <td>0〜99.99 ％</td>
                  </tr>
                </tbody>
              </table>
              <p>*1 小数点一桁まで有効</p>
              <p className="u-mb1em">*2 小数点二桁まで有効</p>
              <p>計算結果が上限値を超える場合は、エラーメッセージが表示されます。</p>
              <table className="c-table">
                <tbody>
                  <tr>
                    <th>計算目標</th>
                    <th>上限値</th>
                  </tr>
                  <tr>
                    <th>評価額</th>
                    <td>
                      999999 万円
                      <br />
                      実際には99.9億円と表示されます。
                    </td>
                  </tr>
                  <tr>
                    <th>必要な利回り</th>
                    <td>999.9 ％</td>
                  </tr>
                  <tr>
                    <th>必要な投資期間</th>
                    <td>99年11ヶ月 （1199ヶ月）</td>
                  </tr>
                  <tr>
                    <th>必要な積立額</th>
                    <td>999万9000 円</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div className="c-modal__overlay" onClick={handleHide}></div>
        </Modal>
      )}
    </React.Fragment>
  )
})
